import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import CustomerLink from '../../Components/CustomerLink';

// สรุปรายการที่ IMD (บัตรขายหน้าโรง) ลงไว้แล้วของวันนี้ — เฉพาะ source=IMD (reference ขึ้นต้น IMD-FC-)
// ใช้ตอนเลือกตัวแทน AGC-TOKYO บนหน้า "สร้างขายจากโรงงาน (Agent)": หน้าโรงลงผ่าน IMD แล้ว ลงที่นี่เฉพาะกรณีคนขายลืมลง
// ⚠ ไม่ปิดปุ่มบันทึก (เผื่อคนขายลืมจริง) — มีแต่ป้ายเตือน + รายการที่ลงไปแล้ว ให้คนเทียบก่อนกดเอง

const ymdLocal = (d = new Date()) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const hhmm = (t) => String(t || '').slice(0, 5);
const money = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// ชื่อร้านที่ลิงก์ไปหน้าลูกค้า (ไม่โชว์รหัส REC-) — AGC-TOKYO = ไม่ระบุร้าน
export const shopLabel = (customerId, nameById) => {
  const id = String(customerId || '');
  if (!id || id.toUpperCase() === 'AGC-TOKYO') return { text: 'ไม่ระบุร้าน', code: null };
  return { text: nameById[id] || null, code: id };
};

// รวม SALE + BAG ของ reference เดียวกันเป็นบรรทัดเดียว (ประกันกระสอบ = reference ของบิล + "-BAG")
export const groupImdRows = (rows) => {
  const imd = (rows || []).filter(r => r && r.source === 'IMD');
  const bags = new Map();
  imd.filter(r => r.kind === 'BAG').forEach(r => bags.set(String(r.reference).replace(/-BAG$/, ''), r));
  return imd
    .filter(r => r.kind === 'SALE')
    .map(r => ({ ...r, bag: bags.get(r.reference) || null }))
    .sort((a, b) => String(b.time).localeCompare(String(a.time)));
};

const FactoryCounterNotice = ({ headers, refreshKey, regularCustomers }) => {
  const [rows, setRows] = useState(null); // null = ยังไม่รู้/อ่านไม่ได้ (ไม่ใช่ "ไม่มี")
  const [error, setError] = useState('');
  const date = ymdLocal();

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/saledata/api/module/front-counter/sales?date=${date}`, { method: 'GET', headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setRows(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      setRows(null);
      setError('อ่านรายการที่ IMD ลงไว้แล้วไม่สำเร็จ — ตรวจจากรายงานบัญชีรายวันก่อนลงซ้ำ');
    }
  }, [headers, date]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const nameById = {};
  (regularCustomers || []).forEach(c => {
    const id = c?.customerUid || c?.uid || c?.id;
    if (id) nameById[id] = c?.storeName || c?.customerName || '';
  });

  const grouped = rows ? groupImdRows(rows) : [];
  const totalQty = grouped.reduce((s, r) => s + (r.quantity || 0), 0);
  const totalAmount = grouped.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <div data-testid="fc-notice" className="btc-card" style={{ padding: '12px 20px', marginBottom: 16, background: '#fff8e1', border: '2px solid #f9a825' }}>
      <div style={{ fontWeight: 700, color: '#8a5a00' }}>
        ⚠ หน้าโรงลงผ่าน IMD แล้ว — ลงที่นี่เฉพาะกรณีคนขายลืมลง ระวังยอดซ้ำ
      </div>
      {error && <div role="alert" style={{ color: '#842029', fontWeight: 600, marginTop: 6 }}>{error}</div>}
      {rows && grouped.length === 0 && (
        <div data-testid="fc-notice-empty" style={{ marginTop: 6, color: '#5f6368' }}>วันนี้ IMD ยังไม่ได้ลงรายการขายหน้าโรง</div>
      )}
      {rows && grouped.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 4 }}>
            IMD ลงไว้แล้ววันนี้ {grouped.length} บิล · {money(totalQty)} กระสอบ · ฿{money(totalAmount)}
          </div>
          <table data-testid="fc-notice-table" style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#5f6368' }}>
                <th>เวลา</th><th>ลูกค้า</th><th style={{ textAlign: 'right' }}>กระสอบ</th><th style={{ textAlign: 'right' }}>ยอดขาย</th><th style={{ textAlign: 'right' }}>ประกัน</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(r => {
                const shop = shopLabel(r.customerId, nameById);
                return (
                  <tr key={r.reference}>
                    <td>{hhmm(r.time)}</td>
                    <td>{shop.code ? <CustomerLink name={shop.text} code={shop.code} /> : shop.text}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.quantity)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {money(r.total)}
                      {r.creditStatus && (
                        <span data-testid="fc-notice-credit" style={{ marginLeft: 6, color: '#b45309', fontWeight: 700 }}>
                          {r.creditStatus === 'PAID' ? 'ค้าง (ชำระแล้ว)' : 'ค้างจ่าย'}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.bag ? money(r.bag.total) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FactoryCounterNotice;
